import React, { useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import Close from '../../../visuals/Icons/Old/Close';
import ButtonIcon from '../../ButtonIcon';
import Input, { IInput } from '../Input';

interface IInputIcon extends IInput {
  onClickIcon?: (
    event: React.MouseEvent<HTMLDivElement | HTMLButtonElement>,
  ) => void;
  onClickIconDisabled?: boolean;
  children: React.ReactNode;
  withIconPointer?: boolean;
  withClearIcon?: boolean;
  onClear?: () => void;
}

const InputIcon: React.FC<IInputIcon> = ({
  children,
  onClickIcon,
  onClickIconDisabled = false,
  withIconPointer = false,
  onClear,
  ...props
}) => {
  // This component can be in same page multiple times. to force focus, we need to target the good one
  const generatedId = useMemo(() => uuid().toString(), []);

  return (
    <>
      <div className="tw-relative tw-w-full">
        <label>
          <Input {...props} id={generatedId} />
        </label>
        {props.withClearIcon ? (
          <span className="icon">
            <span className="actions">
              <span className="clear-icon">
                <ButtonIcon
                  hasShadow={false}
                  width="fit-content"
                  height="fit-content"
                  onClick={() => {
                    document?.getElementById(generatedId)?.focus();

                    onClear?.();
                  }}
                >
                  <Close width={10} />
                </ButtonIcon>
                <span className="separator" />
              </span>
              <ButtonIcon
                hasShadow={false}
                width="fit-content"
                height="fit-content"
                onClick={onClickIcon}
                disabled={onClickIconDisabled}
              >
                {children}
              </ButtonIcon>
            </span>
          </span>
        ) : (
          <span className="icon">
            <ButtonIcon
              hasShadow={false}
              width="fit-content"
              height="fit-content"
              onClick={onClickIcon}
              disabled={onClickIconDisabled}
            >
              {children}
            </ButtonIcon>
          </span>
        )}
      </div>
      <style jsx>{`
        label {
          display: flex;
          position: relative;
        }

        label:after {
          content: '';
          background: linear-gradient(
            to right,
            rgba(255, 255, 255, 0),
            rgba(255, 255, 255, 0.7) 20%,
            #fff 40%
          );
          position: absolute;
          right: 1px;
          top: 2px;
          height: calc(100% - 4px);
          width: 55px;
          border-radius: 26px;
          pointer-events: none;
        }

        span.icon {
          display: block;
          transform: translateY(-50%);
          position: absolute;
          top: 50%;
          right: 15px;
          line-height: 1em;
          background-color: unset;
          outline: none;
          border: none;
        }

        span.icon :global(button) {
          cursor: ${withIconPointer ? 'pointer' : 'default'};
        }

        .actions {
          display: flex;
          flex-direction: row;
          align-items: center;
        }

        .clear-icon {
          display: flex;
          flex-direction: row;
          align-items: center;
        }

        .separator {
          display: block;
          width: 1px;
          border: var(--border);
          height: 28px;
          background-color: var(--clr-tertiary-2);
          margin-right: var(--space-2);
        }
      `}</style>
    </>
  );
};

export default InputIcon;
